import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { CACHE_TIMES, QUERY_KEYS } from "@/hooks/cache/cacheConfig";

export function useOptimizedProfileData(userId?: string) {
  const { user } = useAuth();
  const targetUserId = userId || user?.id;

  const {
    data: profileData,
    isLoading,
    error,
  } = useQuery({
    queryKey: QUERY_KEYS.user.profileOptimized(targetUserId || ""),
    queryFn: async () => {
      if (!targetUserId) return null;

      try {
        // Get public profile with role
        const { data: publicProfile, error: publicError } = await supabase
          .from("public_profiles")
          .select("*")
          .eq("id", targetUserId)
          .single();

        if (publicError) {
          console.error("Error fetching public profile:", publicError);
          return null;
        }

        if (!publicProfile) return null;

        // Get role-specific profile based on user role
        let roleProfile = null;

        if (publicProfile.role === "artist") {
          const { data, error } = await supabase
            .from("artist_profiles")
            .select("*")
            .eq("id", targetUserId)
            .single();

          if (!error && data) {
            roleProfile = data;
          }
        } else if (publicProfile.role === "sponsor") {
          const { data, error } = await supabase
            .from("sponsor_profiles")
            .select("*")
            .eq("id", targetUserId)
            .single();

          if (!error && data) {
            roleProfile = data;
          }
        } else if (publicProfile.role === "fan") {
          const { data, error } = await supabase
            .from("fan_profiles")
            .select("*")
            .eq("id", targetUserId)
            .single();

          if (!error && data) {
            roleProfile = data;
          }
        }

        return {
          publicProfile,
          roleProfile: roleProfile || {}, // Provide empty object if null
          role: publicProfile.role,
        };
      } catch (err) {
        console.error("Error in useOptimizedProfileData:", err);
        return null;
      }
    },
    enabled: !!targetUserId,
    ...CACHE_TIMES.STATIC, // Use static cache for profile data
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Create a combined profile object that matches expected structure
  const profile = profileData
    ? {
        ...profileData.publicProfile,
        ...profileData.roleProfile,
      }
    : null;

  return {
    // Legacy structure for backward compatibility
    profileData,
    isLoadingProfile: isLoading,

    // New expected structure
    profile,
    publicProfile: profileData?.publicProfile || null,
    isLoading,
    error: error?.message || null,
  };
}
